import type * as React from 'react'
import { useEffect, useState } from 'react'
import clsx from 'clsx'
import {
  Flex,
  Button,
  Form,
  Input,
  Text,
  CriticalIcon,
  useDebounceCallback,
} from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'

import style from '../Settings.module.css'
import { organizationNameSchema } from '../validation/organizationName'
import { useUpdateOrganizationName } from '../hooks/useUpdateOrganizationName'
import { useInputValidation } from '../hooks/useInputValidation'

const OrganizationName = (): JSX.Element => {
  const { account } = useAccount()
  const currentOrganizationName = account?.currentOrganization?.name ?? null
  const [newOrganizationName, setNewOrganizationName] = useState(
    currentOrganizationName,
  )

  const { updateOrganizationName } = useUpdateOrganizationName()

  const [invalidInput, setInvalidInput] = useState(false)
  const [isDisabled, setIsDisabled] = useState(
    currentOrganizationName === newOrganizationName,
  )

  const { checkIsValid, isValid, errorMessages } = useInputValidation({
    schema: organizationNameSchema,
    inputValue: newOrganizationName,
  })

  useEffect(() => {
    if (currentOrganizationName === newOrganizationName) {
      setIsDisabled(true)
    } else {
      setIsDisabled(false)
    }
  }, [currentOrganizationName, newOrganizationName])

  const handleSubmit = async (): Promise<void> => {
    const result = await updateOrganizationName({
      organizationId: account?.currentOrganization?.id ?? '',
      name: newOrganizationName?.trim() ?? '',
    })

    if (result) {
      setInvalidInput(false)
    }
  }

  const handleValidateInput = useDebounceCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const inputValue = event.target.value
      const trimmedValue = inputValue.trim()
      setNewOrganizationName(trimmedValue)

      const isValid = checkIsValid({ inputValue: trimmedValue })
      setInvalidInput(!isValid)
    },
    200,
  )

  return (
    <Form
      name="updateOrganizationNameForm"
      className={style.fullWidth}
      onSubmit={handleSubmit}
    >
      <Flex gap="sm" direction="row" align="end">
        <Form.Field
          name="organizationName"
          className={clsx(style.fullWidth, style.field)}
        >
          <Form.Label htmlFor="organizationName">Organization Name</Form.Label>
          <Form.Control>
            <span>
              <Input
                id="organizationName"
                defaultValue={currentOrganizationName ?? ''}
                data-1p-ignore="true"
                aria-invalid={invalidInput}
                onChange={handleValidateInput}
                className={style.formInput}
                disabled={currentOrganizationName === null}
              />
            </span>
          </Form.Control>
        </Form.Field>
        <Button
          size="medium"
          type="submit"
          variant="secondary"
          disabled={isDisabled || !isValid}
          className={style.fixedWidthButton}
        >
          Save Changes
        </Button>
      </Flex>
      {invalidInput &&
        errorMessages &&
        errorMessages.map((error, index) => (
          <Text key={`${index}-org-nameinput-error`} color="critical" size="sm">
            <CriticalIcon size="xsmall" style={{ verticalAlign: 'middle' }} />{' '}
            {error}
          </Text>
        ))}
    </Form>
  )
}

export default OrganizationName
